import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Languages as LanguagesIcon } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import {
  DEFAULT_LANGUAGE_TOGGLES,
  LANGUAGES,
  fetchLanguageToggles,
  saveLanguageToggles,
  useI18n,
  type LanguageToggles,
} from "@/lib/i18n";

export const Route = createFileRoute("/_authenticated/super-admin/languages")({
  head: () => ({
    meta: [
      { title: "Languages · Landoo Super Admin" },
      {
        name: "description",
        content:
          "Enable or disable English, Tamil, Hindi and Telugu for the Landoo interface language selector.",
      },
      { property: "og:title", content: "Languages · Landoo Super Admin" },
      {
        property: "og:description",
        content: "Control which interface languages Landoo users can choose from.",
      },
    ],
  }),
  component: LanguagesPage,
});

function LanguagesPage() {
  const { t } = useI18n();
  const { data, refetch } = useQuery({
    queryKey: ["language-toggles"],
    queryFn: fetchLanguageToggles,
  });
  const [toggles, setToggles] = useState<LanguageToggles>(DEFAULT_LANGUAGE_TOGGLES);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setToggles(data);
  }, [data]);

  const update = async (code: keyof LanguageToggles, value: boolean) => {
    if (code === "en") return;
    const next = { ...toggles, [code]: value };
    setToggles(next);
    setSaving(true);
    try {
      await saveLanguageToggles(next);
      await refetch();
      toast.success(t("msg.saved"));
    } catch {
      setToggles(toggles);
      toast.error(t("msg.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell title={t("lang.title")} role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="max-w-2xl space-y-4">
        <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
          <div className="flex items-center gap-2">
            <LanguagesIcon className="h-4 w-4 text-brown-dark" />
            <h2 className="text-sm font-bold text-brown-dark">{t("lang.title")}</h2>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("lang.subtitle")}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{t("lang.defaultLocked")}</p>

          <ul className="mt-4 divide-y divide-border">
            {LANGUAGES.map((l) => {
              const on = l.code === "en" ? true : toggles[l.code];
              return (
                <li key={l.code} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-brown-dark">{l.label}</div>
                    <div className="text-[11px] text-muted-foreground">{l.english}</div>
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {on ? t("lang.enabled") : t("lang.disabled")}
                  </span>
                  <button
                    disabled={l.code === "en" || saving}
                    onClick={() => update(l.code, !on)}
                    aria-pressed={on}
                    aria-label={`${l.english} ${on ? "enabled" : "disabled"}`}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-50 ${
                      on ? "bg-brand" : "bg-secondary"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-background shadow transition-all ${
                        on ? "left-[22px]" : "left-0.5"
                      }`}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </DashboardShell>
  );
}
